import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzModalService } from 'ng-zorro-antd/modal';
import { PAYMENT_STATUSES } from '@care-portals/data';
import { PaymentStatusKey, IOrder } from '@care-portals/api-interfaces';
import { OrdersFacade } from '../../../+store/order/order.facade';

@Component({
  selector: 'portals-emr-payment-status-modal',
  templateUrl: './payment-status-modal.component.html',
  styleUrls: [],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzButtonModule,
    NzModalModule,
    NzSelectModule,
    NzInputModule,
    NzInputNumberModule
  ]
})
export class PaymentStatusModalComponent implements OnInit, OnChanges {
  @Input() visible = false;
  @Input() order: IOrder | null = null;
  @Input() orderId: number | null = null;
  @Input() currentPaymentStatus: PaymentStatusKey | null = null;
  @Output() visibleChange = new EventEmitter<boolean>();
  retriggerStatusAvailable = true;
  paymentStatusOptions = Object.entries(PAYMENT_STATUSES)
    .filter(s => s[1].show)
    .sort((a, b) => (a[1].sort < b[1].sort ? -1 : 1));
  paymentStatuses = PAYMENT_STATUSES;
  selectedPaymentStatus: PaymentStatusKey | null = null;
  paymentFormData = {
    paymentId: '',
    amount: null as number | null,
    failedReason: '',
    refundId: '',
    refundAmount: null as number | null,
    delayedAmount: null as number | null,
    refundedCreditAmount: null as number | null,
    returnedPostPurchaseCredit: null as number | null
  };

  constructor(
    private modalService: NzModalService,
    private orderFacade: OrdersFacade
  ) {}

  ngOnInit() {
    if (this.visible) {
      this.initializeForm();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['visible'] && this.visible) {
      this.initializeForm();
    }
  }

  initializeForm() {
    if (!this.order) return;

    this.selectedPaymentStatus = this.currentPaymentStatus;

    // Initialize form data with current payment values
    const currentPayment = this.order?.payment || {};
    this.paymentFormData = {
      paymentId: (currentPayment as any)?.paymentId || '',
      amount:
        (currentPayment as any)?.amount || this.order?.totalAmount || null,
      failedReason: (currentPayment as any)?.failedReason || '',
      refundId: (currentPayment as any)?.refundId || '',
      refundAmount: (currentPayment as any)?.refundAmount || null,
      delayedAmount: (currentPayment as any)?.delayedAmount || null,
      refundedCreditAmount:
        (currentPayment as any)?.refundedCreditAmount || null,
      returnedPostPurchaseCredit:
        (currentPayment as any)?.returnedPostPurchaseCredit || null
    };
  }

  closeModal() {
    this.visible = false;
    this.visibleChange.emit(false);
    this.resetForm();
  }

  retriggerStatus() {
    this.modalService.confirm({
      nzTitle: 'Retrigger payment status',
      nzContent: 'Are you sure?',
      nzOkText: 'Yes',
      nzCancelText: 'Cancel',
      nzOnOk: () => {

        this.orderFacade.retriggerPaymentStatus(this.order.id);
        this.retriggerStatusAvailable = false;
      }
    });
  }

  resetForm() {
    this.selectedPaymentStatus = null;
    this.paymentFormData = {
      paymentId: '',
      amount: null,
      failedReason: '',
      refundId: '',
      refundAmount: null,
      delayedAmount: null,
      refundedCreditAmount: null,
      returnedPostPurchaseCredit: null
    };
  }
  retriggerPaymentStatus() {
    this.orderFacade.retriggerPaymentStatus(this.order.id)
  }
  confirmUpdate() {
    if (!this.selectedPaymentStatus) {
      this.modalService.warning({
        nzTitle: 'No Status Selected',
        nzContent: 'Please select a payment status to update.'
      });
      return;
    }

    if (
      this.selectedPaymentStatus === this.currentPaymentStatus &&
      this.paymentFormData.paymentId === (this.order?.payment as any)?.paymentId
    ) {
      this.closeModal();
      return; // No change
    }

    if (!this.orderId) {
      this.modalService.error({
        nzTitle: 'Error',
        nzContent: 'Order ID is missing. Cannot update payment status.'
      });
      return;
    }

    this.savePaymentStatus(this.selectedPaymentStatus, this.paymentFormData);
    this.closeModal();
  }

  savePaymentStatus(
    status: PaymentStatusKey,
    formData: {
      paymentId: string;
      amount: number | null;
      failedReason: string;
      refundId: string;
      refundAmount: number | null;
      delayedAmount: number | null;
      refundedCreditAmount: number | null;
      returnedPostPurchaseCredit: number | null;
    }
  ) {
    const currentPayment = this.order?.payment || {};
    const currentStatus = this.currentPaymentStatus;

    // Build the updated payment object
    const updatedPayment: any = {
      ...currentPayment,
      status,
      paymentId:
        formData?.paymentId || (currentPayment as any)?.paymentId || '',
      gateway: (currentPayment as any)?.gateway || 'stripe' // Preserve existing gateway
    };

    // Set amount if provided, otherwise preserve existing
    if (formData?.amount !== null && formData?.amount !== undefined) {
      updatedPayment.amount = formData.amount;
    } else if (!updatedPayment.amount && this.order?.totalAmount) {
      updatedPayment.amount = this.order.totalAmount;
    }

    // Handle status-specific field updates/clearing
    switch (status) {
      case 'paid':
        // When setting to paid, set paidAt if not already set
        if (!updatedPayment.paidAt) {
          updatedPayment.paidAt = new Date();
        }
        // Clear failed reason if transitioning from failed
        if (currentStatus === 'failed') {
          updatedPayment.failedReason = undefined;
        }
        break;

      case 'failed':
        // Set failedReason from form if provided
        if (formData?.failedReason) {
          updatedPayment.failedReason = formData.failedReason;
        } else if (!updatedPayment.failedReason) {
          // Keep existing failedReason if no new one provided
          updatedPayment.failedReason = (currentPayment as any)?.failedReason;
        }
        break;

      case 'refunded':
      case 'partially_refunded':
        // Set refund-related fields from form if provided
        if (formData?.refundId) {
          updatedPayment.refundId = formData.refundId;
        }
        if (
          formData?.refundAmount !== null &&
          formData?.refundAmount !== undefined
        ) {
          updatedPayment.refundAmount = formData.refundAmount;
        }
        if (
          formData?.refundedCreditAmount !== null &&
          formData?.refundedCreditAmount !== undefined
        ) {
          updatedPayment.refundedCreditAmount = formData.refundedCreditAmount;
        }
        if (
          formData?.returnedPostPurchaseCredit !== null &&
          formData?.returnedPostPurchaseCredit !== undefined
        ) {
          updatedPayment.returnedPostPurchaseCredit =
            formData.returnedPostPurchaseCredit;
        }
        break;

      case 'voided':
        // Clear paidAt and failed reason when voiding
        updatedPayment.paidAt = undefined;
        updatedPayment.failedReason = undefined;
        break;

      case 'pending':
      case 'processing':
      case 'requires_action':
      case 'authorized':
      case 'delayed_capture':
      case 'partial_paid':
        // Set delayedAmount from form if provided
        if (
          formData?.delayedAmount !== null &&
          formData?.delayedAmount !== undefined
        ) {
          updatedPayment.delayedAmount = formData.delayedAmount;
        }
        // For transitional states, preserve existing data
        // Clear paidAt if moving away from paid status
        if (currentStatus === 'paid') {
          updatedPayment.paidAt = undefined;
        }
        // Clear failed reason if moving away from failed
        if (currentStatus === 'failed') {
          updatedPayment.failedReason = undefined;
        }
        break;
      case 'skipped':
        updatedPayment.amount = 0;
        break;
    }

    // Set refund-related fields if provided (for any status)
    if (formData?.refundId) {
      updatedPayment.refundId = formData.refundId;
    }
    if (
      formData?.refundAmount !== null &&
      formData?.refundAmount !== undefined
    ) {
      updatedPayment.refundAmount = formData.refundAmount;
    }
    if (
      formData?.delayedAmount !== null &&
      formData?.delayedAmount !== undefined
    ) {
      updatedPayment.delayedAmount = formData.delayedAmount;
    }
    if (
      formData?.refundedCreditAmount !== null &&
      formData?.refundedCreditAmount !== undefined
    ) {
      updatedPayment.refundedCreditAmount = formData.refundedCreditAmount;
    }
    if (
      formData?.returnedPostPurchaseCredit !== null &&
      formData?.returnedPostPurchaseCredit !== undefined
    ) {
      updatedPayment.returnedPostPurchaseCredit =
        formData.returnedPostPurchaseCredit;
    }

    // Ensure required fields are set
    if (!updatedPayment.paymentId) {
      updatedPayment.paymentId = (currentPayment as any)?.paymentId || '';
    }
    if (!updatedPayment.gateway) {
      updatedPayment.gateway = (currentPayment as any)?.gateway || 'stripe';
    }

    this.orderFacade.updateOrder(this.orderId!, {
      payment: updatedPayment
    });
  }

  get paymentStatus(): PaymentStatusKey | null {
    return this.currentPaymentStatus;
  }
}
